import { User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { bindCandidateActions, type CandidateActions, CandidateDecision } from './CandidateDecision'
import { CandidateOwnerLine } from './CandidateOwner'
import { describeQualityFlags } from './format'
import { MatchScore } from './MatchScore'
import type { StarCandidate } from './StarView'

const base = 'cases.investigate.entityResolution.photoMatching'

export interface MatchDetailsPanelProps {
  /** The very candidates drawn on the star, in the same order. */
  candidates: StarCandidate[]
  /** Open the document detail overlay on a candidate's face. */
  onOpen: (faceId: string) => void
  /**
   * Confirm, reject or merge a candidate, and the face in flight. Omitted for a
   * viewer (PRD-0032 SC-036): the rows then only show and open the candidates.
   */
  actions?: CandidateActions
  /** The candidate last opened in the overlay. */
  selectedFaceId?: string | null
  /** The candidate hovered or focused anywhere — here or on the star. */
  activeFaceId?: string | null
  /** Hovering or focusing a row reports it, so the star node can follow. */
  onActiveChange?: (faceId: string | null) => void
  className?: string
}

/**
 * The details of every candidate on the star: crop, similarity — with the
 * *suggested match* label where the API gave it — quality flags, source photo
 * and page, plus the decision when one is offered. A face already confirmed to
 * another identity names that identity and offers the merge instead of the
 * confirm (ADR-0042). Hover and focus are reported up, so the row and its star
 * node highlight together.
 *
 * Pure — thumbnails arrive as blob URLs, decisions are the caller's callbacks,
 * so the ER tab and the identity page mount the same panel.
 */
export function MatchDetailsPanel({
  candidates,
  onOpen,
  actions,
  selectedFaceId = null,
  activeFaceId = null,
  onActiveChange,
  className,
}: MatchDetailsPanelProps) {
  const { t } = useTranslation()

  if (candidates.length === 0) {
    return <p className="px-1 py-4 text-sm text-muted-foreground">{t(`${base}.star.empty`)}</p>
  }

  return (
    <section
      className={cn('flex min-w-0 flex-col gap-2', className)}
      aria-label={t(`${base}.panel.title`)}
    >
      <h3 className="text-2xs uppercase tracking-wider text-muted-foreground">
        {t(`${base}.panel.title`)}
      </h3>
      <ul className="flex flex-col gap-2">
        {candidates.map(candidate => (
          <MatchDetailsRow
            key={candidate.faceId}
            candidate={candidate}
            active={candidate.faceId === activeFaceId || candidate.faceId === selectedFaceId}
            panel={{ onOpen, actions, onActiveChange }}
          />
        ))}
      </ul>
    </section>
  )
}

/** One candidate of the panel; *panel* is what every row shares. */
function MatchDetailsRow({
  candidate,
  active,
  panel,
}: {
  candidate: StarCandidate
  active: boolean
  panel: Pick<MatchDetailsPanelProps, 'onOpen' | 'actions' | 'onActiveChange'>
}) {
  const { t } = useTranslation()
  const {
    faceId,
    imageUrl,
    score,
    documentName,
    pageNumber,
    qualityFlags = [],
    assignedTo,
  } = candidate
  const { onOpen, actions, onActiveChange } = panel
  const flags = describeQualityFlags(qualityFlags, t, t(`${base}.qualityFlags.none`))

  return (
    <li
      data-face-id={faceId}
      data-active={active ? 'true' : undefined}
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border bg-card p-2 transition-colors',
        active && 'border-primary bg-muted'
      )}
      onMouseEnter={() => onActiveChange?.(faceId)}
      onMouseLeave={() => onActiveChange?.(null)}
    >
      {/* On a phone the decision moves under the candidate as one full-width row,
          so the similarity, its badge and the source keep the card's width. */}
      <div className="flex items-start gap-3 max-md:flex-wrap">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={() => onOpen(faceId)}
          onFocus={() => onActiveChange?.(faceId)}
          onBlur={() => onActiveChange?.(null)}
          aria-label={t(`${base}.panel.open`, { document: documentName })}
        >
          <span className="relative size-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="relative z-10 size-full object-cover" />
            ) : (
              <span className="absolute inset-0 flex items-center justify-center">
                <User className="size-1/2 text-muted-foreground" />
              </span>
            )}
          </span>
          <span className="flex min-w-0 flex-col gap-1">
            <span className="flex flex-wrap items-baseline gap-1.5 text-sm">
              <span className="text-xs text-muted-foreground">
                {t(`${base}.columns.similarity`)}
              </span>
              <MatchScore score={score} suggestedMatch={candidate.suggestedMatch} />
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {pageNumber === null || pageNumber === undefined
                ? documentName
                : t(`${base}.panel.source`, { document: documentName, page: pageNumber })}
            </span>
            <span className="truncate text-xs text-muted-foreground">{flags}</span>
          </span>
        </button>
        {actions ? (
          <CandidateDecision
            assigned={Boolean(assignedTo)}
            disabled={actions.pendingFaceId === faceId}
            on={bindCandidateActions(actions, faceId)}
            // The card is narrow: the full "merge identities" would squeeze the score
            // and its badge under the buttons. The owner line below says whose face
            // it is, and the dialog names both identities before anything merges.
            labels={{
              confirm: t(`${base}.actions.confirm`),
              reject: t(`${base}.actions.reject`),
              merge: t(`${base}.merge.confirm`),
            }}
            className="shrink-0 flex-col max-md:w-full max-md:flex-row max-md:gap-2 max-md:[&>button]:h-9 max-md:[&>button]:flex-1"
          />
        ) : null}
      </div>
      {/* Outside the open button: a link cannot sit inside a button. */}
      {assignedTo ? <CandidateOwnerLine owner={assignedTo} /> : null}
    </li>
  )
}
